import { useEffect, useState, useCallback, useRef } from "react";
import { useParams, useSearchParams, Link } from "react-router-dom";
import { Check, Unlock, ArrowLeft, ArrowRight, Loader2, Sparkles, Shield, Lock, ImageIcon, Download, Share2, MessageCircle, Film, Palette, Camera, Zap, ChevronRight, X, Users, Flame } from "lucide-react";
import NavBar from "../components/NavBar";
import { getAnalysis, testUnlock } from "../lib/api";
import { usePricing, formatINR } from "../lib/pricing";
import { PriceLine, LaunchPill } from "../components/PricingBadge";
import { FreeReport, PaidReport } from "../components/Reports";
import StoryStudioForm from "../components/StoryStudioForm";
import { toast } from "sonner";

// ---------------------------------------------------------------------------
// Conversion-push components — Feb 2026 uplift
// ---------------------------------------------------------------------------

/** Personalised H1 using bride + groom first names, framed forward (no personalized preview exists pre-payment). */
function buildPersonalisedHeadline(analysis) {
    const q = analysis?.questionnaire || {};
    const bRaw = (q.bride_name || analysis?.bride_name || "").trim();
    const gRaw = (q.groom_name || analysis?.groom_name || "").trim();
    const b = bRaw.split(/\s+/)[0];
    const g = gRaw.split(/\s+/)[0];
    if (b && g) return `${b} & ${g}, let's build your wedding look.`;
    if (b) return `${b}, let's build your wedding look.`;
    if (g) return `${g}, let's build your wedding look.`;
    return "Let's build your wedding look.";
}

/** Slots-left + launch-price urgency banner. Recomputed live from usePricing(). */
function ScarcityBanner({ priceLabel }) {
    const p = usePricing();
    if (!p?.is_launch) return null;
    const left = p.slots_left ?? 0;
    const pct = p.slots_total ? Math.max(4, Math.min(100, (1 - left / p.slots_total) * 100)) : 0;
    return (
        <div
            className="mt-4 overflow-hidden rounded-[18px] bg-charcoal p-4 text-ivory ring-1 ring-gold/30 md:p-5"
            data-testid="scarcity-banner"
        >
            <div className="flex flex-wrap items-center justify-between gap-3">
                <div className="flex items-center gap-2.5">
                    <span className="inline-flex h-7 w-7 flex-none items-center justify-center rounded-full bg-gold/20 text-gold">
                        <Flame className="h-3.5 w-3.5" />
                    </span>
                    <div className="text-[13px] leading-snug">
                        <span className="font-medium text-ivory">Launch pricing · {priceLabel}</span>
                        <span className="ml-2 text-ivory/70">₹250 off — only <span className="font-semibold text-gold">{left}</span> of {p.slots_total} slots left</span>
                    </div>
                </div>
                <span className="text-[10.5px] uppercase tracking-[0.22em] text-ivory/60">
                    Then jumps to ₹1,499
                </span>
            </div>
            <div className="mt-3 h-1 w-full overflow-hidden rounded-full bg-ivory/10">
                <div className="h-full rounded-full bg-gold" style={{ width: `${pct}%` }} />
            </div>
        </div>
    );
}

/** Live-feel social proof — reads paid_count from /api/config pricing block. */
function SocialProofTicker() {
    const p = usePricing();
    const paidCount = p?.paid_count ?? 0;
    if (paidCount <= 0) return null;
    // Two static seed rows + a running counter feel alive without a fake feed.
    const items = [
        { name: "Anjali & Rahul",  when: "2 hours ago",  city: "Bengaluru" },
        { name: "Neha & Aditya",   when: "8 hours ago",  city: "Mumbai" },
        { name: "Priya & Kunal",   when: "yesterday",    city: "Delhi" },
        { name: `${paidCount} couples`, when: "already unlocked their full strategy", city: "" },
    ];
    return (
        <div className="mt-4 overflow-hidden rounded-full bg-white/70 px-4 py-2 shadow-soft ring-1 ring-charcoal/5" data-testid="social-proof-ticker">
            <div className="flex items-center gap-3 text-[12.5px] text-ink">
                <span className="inline-flex h-6 w-6 flex-none items-center justify-center rounded-full bg-emerald-50 text-emerald-600 ring-1 ring-emerald-100">
                    <Users className="h-3.5 w-3.5" />
                </span>
                <div className="marquee-track flex flex-nowrap items-center gap-8 whitespace-nowrap">
                    {items.concat(items).map((it, i) => (
                        <span key={i} className="inline-flex items-center gap-2">
                            <span className="h-1.5 w-1.5 rounded-full bg-gold" />
                            <span className="font-medium text-charcoal">{it.name}</span>
                            <span className="text-charcoal/60">{it.when}</span>
                            {it.city && <span className="text-charcoal/40">· {it.city}</span>}
                        </span>
                    ))}
                </div>
            </div>
        </div>
    );
}

/** Always-visible unlock CTA anchored to the bottom of the viewport. */
function StickyUnlockBar({ priceLabel, onUnlock }) {
    return (
        <div
            className="fixed inset-x-0 bottom-0 z-40 border-t border-charcoal/10 bg-white/95 px-4 py-3 shadow-[0_-10px_30px_rgba(23,20,17,0.08)] backdrop-blur md:px-8"
            data-testid="sticky-unlock-bar"
        >
            <div className="mx-auto flex max-w-6xl items-center justify-between gap-3">
                <div className="min-w-0 flex-1">
                    <div className="truncate text-[13px] font-medium text-charcoal">
                        Build your wedding vision
                    </div>
                    <div className="mt-0.5 text-[11px] text-ink/70">
                        From {priceLabel} · pick your event pack above
                    </div>
                </div>
                <button
                    type="button"
                    onClick={onUnlock}
                    data-testid="sticky-unlock-cta"
                    className="inline-flex flex-none items-center gap-2 rounded-full bg-gold px-5 py-3 text-[13px] font-medium text-charcoal shadow-soft transition hover:bg-goldDark md:px-7"
                >
                    <Zap className="h-4 w-4" /> Choose a pack <ChevronRight className="hidden h-4 w-4 md:inline" />
                </button>
            </div>
        </div>
    );
}

/** Fires once per session when the user tries to leave (mouse-out on top edge). */
function ExitIntentModal({ priceLabel, onUnlock, analysis }) {
    const [open, setOpen] = useState(false);
    const firedRef = useRef(false);

    useEffect(() => {
        if (typeof window === "undefined") return;
        if (sessionStorage.getItem("vowze:exitFired") === "1") return;
        const onLeave = (e) => {
            if (firedRef.current) return;
            // Fire only when the pointer exits through the top edge — the
            // classic "reaching for the tab" gesture.
            if (e.clientY <= 4 && (e.relatedTarget === null || e.relatedTarget === undefined)) {
                firedRef.current = true;
                sessionStorage.setItem("vowze:exitFired", "1");
                setOpen(true);
            }
        };
        document.addEventListener("mouseout", onLeave);
        return () => document.removeEventListener("mouseout", onLeave);
    }, []);

    if (!open) return null;
    const q = analysis?.questionnaire || {};
    const b = (q.bride_name || analysis?.bride_name || "").trim().split(/\s+/)[0];
    const g = (q.groom_name || analysis?.groom_name || "").trim().split(/\s+/)[0];
    const headline = b && g
        ? `Wait — ${b} & ${g}, one thing before you go.`
        : "Wait — one thing before you go.";

    return (
        <div
            className="fixed inset-0 z-50 flex items-center justify-center bg-charcoal/60 p-4 backdrop-blur-sm"
            data-testid="exit-intent-modal"
            role="dialog"
            aria-modal="true"
        >
            <div className="relative w-full max-w-lg rounded-[28px] bg-white p-8 shadow-2xl md:p-10">
                <button
                    type="button"
                    onClick={() => setOpen(false)}
                    aria-label="Close"
                    data-testid="exit-intent-close"
                    className="absolute right-4 top-4 inline-flex h-8 w-8 items-center justify-center rounded-full bg-charcoal/5 text-charcoal/60 transition hover:bg-charcoal/10"
                >
                    <X className="h-4 w-4" />
                </button>
                <div className="flex items-center gap-2 text-gold">
                    <Sparkles className="h-4 w-4" />
                    <span className="text-[10.5px] uppercase tracking-[0.28em]">One-time offer · this tab only</span>
                </div>
                <h3 className="mt-4 font-serif text-3xl leading-tight text-charcoal md:text-[32px]">
                    {headline}
                </h3>
                <p className="mt-4 text-[15px] leading-relaxed text-ink">
                    You've seen the sample report and your event pack pricing. The <span className="font-medium text-charcoal">full plan</span> — with your faces rendered across every event — is 90 seconds away.
                </p>
                <div className="mt-6 flex flex-wrap items-center gap-2 text-[12.5px] text-ink">
                    <span className="inline-flex items-center gap-1.5 rounded-full bg-emerald-50 px-3 py-1 text-emerald-700 ring-1 ring-emerald-100">
                        <Check className="h-3 w-3" /> Full 12-colour palette + HEX + fabric weights
                    </span>
                    <span className="inline-flex items-center gap-1.5 rounded-full bg-emerald-50 px-3 py-1 text-emerald-700 ring-1 ring-emerald-100">
                        <Check className="h-3 w-3" /> 4 AI portraits per event · your faces
                    </span>
                </div>
                <button
                    type="button"
                    onClick={() => { setOpen(false); onUnlock(); }}
                    data-testid="exit-intent-cta"
                    className="mt-7 inline-flex w-full items-center justify-center gap-2 rounded-full bg-gold px-8 py-4 text-[14px] font-medium text-charcoal transition hover:bg-goldDark"
                >
                    <Zap className="h-4 w-4" /> Unlock full report · {priceLabel}
                </button>
                <button
                    type="button"
                    onClick={() => setOpen(false)}
                    data-testid="exit-intent-dismiss"
                    className="mt-3 w-full text-center text-[12px] text-ink/60 transition hover:text-ink"
                >
                    I'll come back later
                </button>
            </div>
        </div>
    );
}

// ---------------------------------------------------------------------------
// Paid-report toppers — Photo outcome hero (h) + Executive summary (j)
// ---------------------------------------------------------------------------
export function PhotoOutcomeHero({ analysis, paidReport }) {
    // Use hero.headline if Claude produced one; else compose from moods.
    const q = analysis?.questionnaire || {};
    const b = (q.bride_name || analysis?.bride_name || "").trim().split(/\s+/)[0];
    const g = (q.groom_name || analysis?.groom_name || "").trim().split(/\s+/)[0];
    const moods = extractMoods(paidReport);
    const heroImg = extractHeroImageUrl(analysis);
    const namesLine = b && g ? `${b} & ${g}` : b || g || "Your wedding";
    return (
        <div className="relative overflow-hidden rounded-[28px] bg-charcoal text-ivory shadow-soft" data-testid="photo-outcome-hero">
            <div className="grid gap-0 md:grid-cols-[1.15fr_1fr]">
                <div className="p-8 md:p-10">
                    <div className="flex items-center gap-2 text-gold">
                        <Sparkles className="h-4 w-4" />
                        <span className="text-[10.5px] uppercase tracking-[0.28em]">Predicted photo outcome</span>
                    </div>
                    <h2 className="mt-4 font-serif text-3xl leading-tight md:text-[38px] md:leading-[1.1]">
                        {namesLine}, this is how your album will feel.
                    </h2>
                    {moods.length > 0 && (
                        <div className="mt-5 flex flex-wrap gap-2" data-testid="photo-outcome-moods">
                            {moods.map((m, i) => (
                                <span key={i} className="inline-flex items-center gap-1.5 rounded-full bg-gold/15 px-3 py-1.5 text-[12px] font-medium uppercase tracking-[0.16em] text-gold ring-1 ring-gold/25">
                                    {m}
                                </span>
                            ))}
                        </div>
                    )}
                    <p className="mt-5 text-[14px] leading-relaxed text-ivory/80">
                        Every colour, fabric, and pose recommendation below is engineered to land your album in this exact emotional register — not just correct, but unforgettable.
                    </p>
                </div>
                <div className="relative min-h-[260px] md:min-h-[380px]">
                    {heroImg ? (
                        <img
                            src={heroImg}
                            alt="Your predicted wedding portrait"
                            className="absolute inset-0 h-full w-full object-cover"
                            loading="lazy"
                        />
                    ) : (
                        <div className="absolute inset-0 bg-gradient-to-br from-gold/30 via-charcoal/70 to-charcoal" />
                    )}
                    <div className="pointer-events-none absolute inset-0 bg-gradient-to-r from-charcoal via-charcoal/30 to-transparent md:bg-gradient-to-l md:from-transparent md:via-transparent md:to-charcoal/70" />
                </div>
            </div>
        </div>
    );
}

export function ExecutiveSummary({ analysis, paidReport }) {
    const moves = extractPowerMoves(paidReport);
    if (moves.length === 0) return null;
    return (
        <div className="rounded-[24px] bg-white p-6 shadow-soft md:p-8" data-testid="executive-summary">
            <div className="flex items-center gap-2 text-gold">
                <Zap className="h-4 w-4" />
                <span className="text-[10.5px] uppercase tracking-[0.28em]">3 power moves — start here</span>
            </div>
            <h3 className="mt-3 font-serif text-2xl leading-snug text-charcoal md:text-[28px]">
                If you do only three things from this report, do these.
            </h3>
            <ol className="mt-6 grid gap-4 md:grid-cols-3">
                {moves.slice(0, 3).map((m, i) => (
                    <li key={i} className="rounded-[18px] bg-champagne/30 p-5 ring-1 ring-charcoal/5">
                        <div className="flex items-center gap-2 text-gold">
                            <span className="inline-flex h-7 w-7 items-center justify-center rounded-full bg-gold/15 font-serif text-[14px]">
                                {i + 1}
                            </span>
                            <span className="text-[10.5px] uppercase tracking-[0.22em] text-charcoal/60">Move {i + 1}</span>
                        </div>
                        <p className="mt-3 text-[14px] leading-relaxed text-charcoal">{m}</p>
                    </li>
                ))}
            </ol>
        </div>
    );
}

// ---------------------------------------------------------------------------
// Paid-report helpers — best-effort extractors that survive schema drift.
// ---------------------------------------------------------------------------
function extractMoods(paidReport) {
    if (!paidReport) return [];
    // Try a few common shapes we might already produce.
    const bag = [];
    const push = (v) => { if (typeof v === "string" && v.trim()) bag.push(v.trim()); };
    push(paidReport?.moodboard?.vibe_words);
    (paidReport?.moodboard?.mood_tags || []).forEach(push);
    (paidReport?.hero?.mood_tags || []).forEach(push);
    // Fall back to a single-string CSV.
    if (typeof paidReport?.moodboard?.vibe === "string") bag.push(paidReport.moodboard.vibe);
    const flat = bag
        .flatMap((s) => s.split(/[·,•|]/))
        .map((s) => s.trim())
        .filter(Boolean);
    // Dedupe, cap to 3.
    return Array.from(new Set(flat)).slice(0, 3);
}

function extractPowerMoves(paidReport) {
    if (!paidReport) return [];
    // Primary path — the schema we actually emit.
    const fdb = paidReport?.full_decision_block || {};
    const candidates = [
        fdb.what_to_fix,
        fdb.what_will_look_premium,
        paidReport?.decision_engine?.what_to_fix,
        paidReport?.what_to_fix,
        paidReport?.executive_summary,
    ];
    for (const raw of candidates) {
        if (Array.isArray(raw)) {
            const cleaned = raw.filter((s) => typeof s === "string" && s.trim()).slice(0, 3);
            if (cleaned.length) return cleaned;
        }
        if (typeof raw === "string" && raw.trim()) return [raw.trim()];
    }
    // Last resort — mine biggest_mistake_warning as a single actionable line.
    if (typeof paidReport?.biggest_mistake_warning === "string" && paidReport.biggest_mistake_warning.trim()) {
        return [paidReport.biggest_mistake_warning.trim()];
    }
    return [];
}

function extractHeroImageUrl(analysis) {
    const g = analysis?.gallery || {};
    const candidates = ["event_1_couple", "event_1_bride", "event_1_groom", "couple_hero", "hero_couple"];
    for (const k of candidates) {
        const url = g[k];
        if (typeof url === "string" && url) {
            return url.startsWith("http") ? url : (analysis?._absBase ? `${analysis._absBase}${url}` : url);
        }
    }
    return null;
}


const TONE_LABELS = {
    fair: "Fair",
    light_medium: "Light-medium",
    medium: "Medium",
    medium_deep: "Medium-deep",
    deep: "Deep",
};
const UNDERTONE_LABELS = {
    warm: "Warm",
    cool: "Cool",
    olive: "Olive",
    neutral: "Neutral",
};

function SkinTonePerson({ label, data }) {
    if (!data || data.status !== "ok") {
        return (
            <div className="flex items-center gap-3 rounded-[14px] bg-champagne/50 p-3 text-[12px] text-ink">
                <span className="inline-block h-3 w-3 rounded-full bg-ink/30" />
                {label}: unable to read skin tone — {data?.recommendation || "retry with a clearer photo."}
            </div>
        );
    }
    return (
        <div className="flex items-center gap-4 rounded-[16px] bg-champagne/50 p-4" data-testid={`skintone-${label.toLowerCase()}`}>
            <div
                className="h-12 w-12 flex-none rounded-xl ring-1 ring-black/5"
                style={{ background: data.dominant_hex }}
                title={data.dominant_hex}
            />
            <div className="flex-1">
                <div className="text-[10px] uppercase tracking-[0.22em] text-charcoal/60">{label}</div>
                <div className="mt-1 text-sm text-charcoal">
                    <span className="font-medium">{TONE_LABELS[data.tone] || data.tone}</span>
                    <span className="text-ink"> · {UNDERTONE_LABELS[data.undertone] || data.undertone} undertone</span>
                </div>
                <div className="mt-0.5 text-[11px] text-ink">
                    Brightness {data.brightness} · Contrast {data.contrast} · {data.dominant_hex}
                </div>
            </div>
            <div className="text-right text-[11px] text-ink">
                <div>conf.</div>
                <div className="font-mono text-charcoal">{data.confidence}</div>
            </div>
        </div>
    );
}

function SkinToneCard({ analysis }) {
    const st = analysis.skin_tone;
    if (!st) return null;
    const isCouple = analysis.mode === "couple";
    return (
        <div className="rounded-[28px] bg-white p-8 shadow-soft md:p-10" data-testid="skintone-card">
            <div className="flex items-center justify-between">
                <span className="eyebrow">Skin tone read</span>
                <span className="rounded-full bg-champagne px-3 py-1 text-[11px] text-charcoal/70">
                    Deterministic · no AI
                </span>
            </div>
            <h3 className="mt-5 font-serif text-2xl leading-tight text-charcoal">
                Your actual colour data, in numbers.
            </h3>
            <p className="mt-2 text-sm text-ink">
                Extracted from cheek + forehead skin patches in LAB colour space — the same signal our strategy is calibrated against.
            </p>
            <div className="mt-6 space-y-3">
                {isCouple ? (
                    <>
                        <SkinTonePerson label="Bride" data={st.bride} />
                        <SkinTonePerson label="Groom" data={st.groom} />
                        {st.couple_signal && (
                            <div className="mt-2 rounded-[14px] bg-ivory p-3 text-[12px] text-charcoal">
                                Tonal relation:{" "}
                                <strong className="capitalize">
                                    {st.couple_signal.tonal_relation.replace("_", " ")}
                                </strong>
                                <span className="ml-2 text-ink">
                                    ΔL {st.couple_signal.delta_L} · ΔE {st.couple_signal.delta_e}
                                </span>
                            </div>
                        )}
                    </>
                ) : (
                    <SkinTonePerson label={analysis.name || "You"} data={st} />
                )}
            </div>
        </div>
    );
}

function SampleReportTeaser() {
    return (
        <div className="mt-8 rounded-[28px] bg-charcoal p-8 text-ivory shadow-soft md:p-10" data-testid="sample-report-teaser">
            <span className="text-[11px] uppercase tracking-[0.25em] text-gold">Explore a Sample Vowze Report</span>
            <p className="mt-3 max-w-xl text-[15px] leading-relaxed text-ivory/75">
                See how Vowze transforms personal colour analysis, coordinated wedding styling and
                event-specific visualizations into a personalized wedding vision.
            </p>
            <Link
                to="/sample-report"
                target="_blank"
                rel="noopener noreferrer"
                className="mt-5 inline-flex items-center gap-2 rounded-full bg-gold px-6 py-3 text-[12px] font-semibold uppercase tracking-[0.18em] text-charcoal transition hover:bg-gold/90"
                data-testid="view-sample-report-btn"
            >
                View Sample Report <ArrowRight className="h-3.5 w-3.5" />
            </Link>
        </div>
    );
}

function FullReport({ analysis }) {
    const { full } = analysis;
    if (!full) return null;
    return (
        <div className="rounded-[28px] bg-charcoal p-8 text-ivory shadow-soft md:p-10" data-testid="full-report">
            <div className="flex items-center justify-between">
                <span className="text-[11px] uppercase tracking-[0.25em] text-gold">Full strategy — unlocked</span>
                <span className="rounded-full bg-gold/20 px-3 py-1 text-[11px] text-gold">
                    Couple
                </span>
            </div>

            <div className="mt-7">
                <div className="text-[11px] uppercase tracking-[0.22em] text-ivory/60">Color strategy</div>
                <p className="mt-2 text-[15px] leading-relaxed text-ivory/90">{full.color_strategy}</p>
            </div>

            {full.outfit_plans && (
                <div className="mt-8 grid gap-4 md:grid-cols-3">
                    {["safe", "premium", "bold"].map(
                        (k) =>
                            full.outfit_plans?.[k] && (
                                <div key={k} className="rounded-2xl bg-ivory/5 p-5">
                                    <div className="text-[10px] uppercase tracking-[0.22em] text-gold">{k}</div>
                                    <div className="mt-2 text-[14px] leading-snug text-ivory/90">{full.outfit_plans[k]}</div>
                                </div>
                            )
                    )}
                </div>
            )}

            {full.occasion_looks?.length > 0 && (
                <div className="mt-8">
                    <div className="text-[11px] uppercase tracking-[0.22em] text-ivory/60">Occasion looks</div>
                    <ul className="mt-3 grid gap-3 md:grid-cols-2">
                        {full.occasion_looks.map((o) => (
                            <li key={o} className="flex items-start gap-3 text-[14px] text-ivory/85">
                                <Check className="mt-0.5 h-4 w-4 flex-none text-gold" />
                                <span>{o}</span>
                            </li>
                        ))}
                    </ul>
                </div>
            )}

            <div className="mt-8 grid gap-6 md:grid-cols-2">
                {full.grooming_tips?.length > 0 && (
                    <div>
                        <div className="text-[11px] uppercase tracking-[0.22em] text-ivory/60">Grooming & styling</div>
                        <ul className="mt-3 space-y-2 text-[14px] text-ivory/85">
                            {full.grooming_tips.map((t) => (
                                <li key={t} className="flex items-start gap-3">
                                    <Check className="mt-0.5 h-4 w-4 flex-none text-gold" />
                                    <span>{t}</span>
                                </li>
                            ))}
                        </ul>
                    </div>
                )}
                {full.mistakes_to_avoid?.length > 0 && (
                    <div>
                        <div className="text-[11px] uppercase tracking-[0.22em] text-ivory/60">Mistakes to avoid</div>
                        <ul className="mt-3 space-y-2 text-[14px] text-ivory/85">
                            {full.mistakes_to_avoid.map((t) => (
                                <li key={t} className="flex items-start gap-3">
                                    <Check className="mt-0.5 h-4 w-4 flex-none text-gold" />
                                    <span>{t}</span>
                                </li>
                            ))}
                        </ul>
                    </div>
                )}
            </div>

            <div className="mt-8 space-y-6 border-t border-ivory/10 pt-8">
                {full.coordination_plan && (
                    <div>
                        <div className="text-[11px] uppercase tracking-[0.22em] text-ivory/60">Coordination plan</div>
                        <p className="mt-2 text-[15px] leading-relaxed text-ivory/90">{full.coordination_plan}</p>
                    </div>
                )}
                {full.contrast_optimization && (
                    <div>
                        <div className="text-[11px] uppercase tracking-[0.22em] text-ivory/60">Contrast optimization</div>
                        <p className="mt-2 text-[15px] leading-relaxed text-ivory/90">{full.contrast_optimization}</p>
                    </div>
                )}
                {full.background_decor && (
                    <div>
                        <div className="text-[11px] uppercase tracking-[0.22em] text-ivory/60">Background & decor match</div>
                        <p className="mt-2 text-[15px] leading-relaxed text-ivory/90">{full.background_decor}</p>
                    </div>
                )}
                {full.photo_outcome_prediction && (
                    <div>
                        <div className="text-[11px] uppercase tracking-[0.22em] text-ivory/60">Photo outcome prediction</div>
                        <p className="mt-2 text-[15px] leading-relaxed text-ivory/90">{full.photo_outcome_prediction}</p>
                    </div>
                )}
            </div>
        </div>
    );
}

// ---------- Wedding Look Gallery (paid only) ----------
const BRIDE_SLOTS = [
    { k: "bride_engagement", label: "Engagement" },
    { k: "bride_mehendi", label: "Haldi / Mehendi" },
    { k: "bride_sangeet", label: "Sangeet" },
    { k: "bride_reception", label: "Reception" },
];
const GROOM_SLOTS = [
    { k: "groom_engagement", label: "Engagement" },
    { k: "groom_mehendi", label: "Haldi / Mehendi" },
    { k: "groom_sangeet", label: "Sangeet" },
    { k: "groom_reception", label: "Reception" },
];

// Phase C.3 · dynamic per-event portraits. Slot keys are stable (event_1 …
// event_N). Labels are derived at render time from the record's
// questionnaire.events_brief[i]. Backdrops are rendered last, inside PaidReport.
const BACKDROP_SLOTS = [
    { k: "backdrop_mandap", label: "Mandap reference", tag: "Marigold + mogra" },
    { k: "backdrop_baraat", label: "Festive backdrop", tag: "Haveli · baraat mood" },
];
// Slot keys that indicate a Phase C+ record (any version >= C.1). Used to
// route rendering to <WeddingLookGalleryPhaseC> vs the legacy v1 layout.
const NEW_SLOT_KEYS = [
    // v5 (current) — 4 slots per event
    "event_1_bride", "event_1_groom", "event_1_couple", "event_1_backdrop",
    "event_2_bride", "event_2_groom", "event_2_couple", "event_2_backdrop",
    "event_3_bride", "event_3_groom", "event_3_couple", "event_3_backdrop",
    "event_4_bride", "event_4_groom", "event_4_couple", "event_4_backdrop",
    "event_5_bride", "event_5_groom", "event_5_couple", "event_5_backdrop",
    // v4 event_i single slot
    "event_1", "event_2", "event_3", "event_4", "event_5",
    // v3 named-event slots + shared backdrops
    "couple_traditional", "couple_luxury", "couple_modern", "couple_editorial", "couple_destination",
    "couple_engagement", "couple_haldi", "couple_sangeet", "couple_wedding", "couple_reception",
    "backdrop_primary", "backdrop_alt", "backdrop_mandap", "backdrop_baraat",
];
function hasNewGallerySlots(gallery) {
    if (!gallery || typeof gallery !== "object") return false;
    return NEW_SLOT_KEYS.some((k) => Boolean(gallery[k]));
}

// Turn a record's questionnaire.events_brief into the ordered slot list the
// gallery renders. Falls back to a generic 5-portrait labelling for legacy
// records that don't have events_brief.
function buildEventSlots(questionnaire) {
    const eb = Array.isArray(questionnaire?.events_brief) ? questionnaire.events_brief : [];
    if (eb.length > 0) {
        return eb.slice(0, 5).map((ev, i) => ({
            k: `event_${i + 1}`,
            label: (ev?.name || `Event ${i + 1}`).replace(/\b\w/g, (c) => c.toUpperCase()),
            tag: [ev?.venue_type, ev?.style].filter(Boolean).join(" · "),
        }));
    }
    // Legacy fallback — treat the 5 old Phase C.2 slots as event_1..5 order.
    return [
        { k: "event_1", label: "Wedding",     tag: "Main ceremony" },
        { k: "event_2", label: "Reception",   tag: "Evening" },
        { k: "event_3", label: "Engagement",  tag: "Pre-wedding" },
        { k: "event_4", label: "Sangeet",     tag: "Festive" },
        { k: "event_5", label: "Haldi",       tag: "Morning" },
    ];
}
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";

function absGalleryUrl(rel) {
    if (!rel) return null;
    if (rel.startsWith("http")) return rel;
    return `${BACKEND_URL}${rel}`;
}

function GalleryTile({ url, label, generating }) {
    if (url) {
        return (
            <figure className="group relative overflow-hidden rounded-[18px] bg-champagne/40 ring-1 ring-charcoal/5" data-testid={`gallery-${label.toLowerCase().replace(/[^a-z]/g, "-")}`}>
                <img src={url} alt={label} className="aspect-[3/4] w-full object-cover transition duration-700 group-hover:scale-[1.02]" loading="lazy" />
                <figcaption className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-charcoal/85 via-charcoal/30 to-transparent p-3 text-[11px] uppercase tracking-[0.22em] text-ivory">
                    {label}
                </figcaption>
            </figure>
        );
    }
    return (
        <div className="flex aspect-[3/4] w-full flex-col items-center justify-center rounded-[18px] bg-champagne/40 ring-1 ring-charcoal/5 text-charcoal/40">
            {generating ? (
                <>
                    <Loader2 className="h-5 w-5 animate-spin" />
                    <div className="mt-3 text-[10px] uppercase tracking-[0.22em]">{label}</div>
                    <div className="mt-1 text-[10px] text-charcoal/35">Rendering…</div>
                </>
            ) : (
                <>
                    <ImageIcon className="h-5 w-5" />
                    <div className="mt-3 text-[10px] uppercase tracking-[0.22em]">{label}</div>
                    <div className="mt-1 text-[10px] text-charcoal/35">Image unavailable</div>
                </>
            )}
        </div>
    );
}

/** Merge the DB `storyboard` array (final result) with any per-section URLs streamed
 *  under `storyboard_progress.<key>` so the paid report can show sections as they
 *  arrive during a live unlock. If the final `storyboard` isn't populated yet
 *  (Nano Banana is still working on the first section) we still return an empty
 *  array — the WeddingWalkthrough component itself renders nothing until at least
 *  one section is present. */
function mergedStoryboard(analysis) {
    if (!analysis) return [];
    const finalList = Array.isArray(analysis.storyboard) ? analysis.storyboard : null;
    const progress = analysis.storyboard_progress || {};
    if (finalList && finalList.length) {
        return finalList.map((s) => ({
            ...s,
            image_url: absGalleryUrl(s.image_url || progress[s.key]),
        }));
    }
    // No final list yet — synthesise from progress keys (rare edge case where
    // progress arrives before the final $set writes).
    return Object.keys(progress).map((key) => ({
        key,
        title: key.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase()),
        image_url: absGalleryUrl(progress[key]),
        shots: [],
        tag: "portrait",
        number: 0,
    }));
}



function PaletteSwatch({ hex }) {
    return (
        <div className="flex items-center gap-2 rounded-full bg-ivory/5 px-3 py-1.5 text-[11px] text-ivory/90 ring-1 ring-ivory/10">
            <span className="h-3 w-3 rounded-full ring-1 ring-ivory/20" style={{ background: hex }} />
            <span className="font-mono">{hex}</span>
        </div>
    );
}

// Fallback map so records generated during earlier pivots still resolve to a
// usable image when the v5 slot isn't present.
const _EVENT_FALLBACK = {
    event_1_couple: ["event_1", "couple_wedding",     "couple_traditional", "couple_hero"],
    event_2_couple: ["event_2", "couple_reception",   "couple_luxury"],
    event_3_couple: ["event_3", "couple_engagement",  "couple_modern"],
    event_4_couple: ["event_4", "couple_sangeet",     "couple_editorial"],
    event_5_couple: ["event_5", "couple_haldi",       "couple_destination"],
};
function pickSlot(gallery, key) {
    if (!gallery) return null;
    if (gallery[key]) return gallery[key];
    for (const alt of (_EVENT_FALLBACK[key] || [])) {
        if (gallery[alt]) return gallery[alt];
    }
    return null;
}

// Phase C.5 · dynamic per-event couple hero. The signature-portrait grid is
// retired — the per-event portraits now live under <PaidReport> in the new
// Bride/Groom/Couple analysis sections. Here we just show a hero couple shot.
function WeddingLookGalleryPhaseC({ gallery, generating, palette, questionnaire }) {
    const eventSlots = buildEventSlots(questionnaire);
    // Hero = main event's couple photo (event_1_couple) with fallback aliases.
    const heroKey = "event_1_couple";
    const heroSlot = eventSlots[0];
    const heroUrl = absGalleryUrl(pickSlot(gallery, heroKey));

    return (
        <section className="space-y-8" data-testid="wedding-look-gallery">
            {/* Hero + palette side panel */}
            <div className="overflow-hidden rounded-[28px] bg-charcoal text-ivory shadow-soft">
                <div className="grid gap-0 md:grid-cols-2">
                    <div className="relative aspect-[3/4] bg-charcoal md:aspect-auto md:min-h-[520px]">
                        {heroUrl ? (
                            <img
                                src={heroUrl}
                                alt="Couple hero portrait"
                                className="absolute inset-0 h-full w-full object-contain object-center"
                                data-testid="gallery-hero"
                                loading="lazy"
                            />
                        ) : (
                            <div className="absolute inset-0 flex flex-col items-center justify-center bg-ivory/5 text-ivory/40">
                                {generating ? (
                                    <>
                                        <Loader2 className="h-6 w-6 animate-spin" />
                                        <p className="mt-4 text-[11px] uppercase tracking-[0.22em]">Rendering your hero look…</p>
                                        <p className="mt-1 max-w-xs text-center text-[11px] text-ivory/30">Nano Banana Pro is composing every ceremony you selected. ~90s.</p>
                                    </>
                                ) : (
                                    <>
                                        <ImageIcon className="h-6 w-6" />
                                        <p className="mt-3 text-[11px] uppercase tracking-[0.22em]">Hero unavailable</p>
                                    </>
                                )}
                            </div>
                        )}
                    </div>
                    <div className="flex flex-col justify-center p-8 md:p-10">
                        <span className="text-[11px] uppercase tracking-[0.25em] text-gold">
                            Your wedding look · {heroSlot?.label || "Main ceremony"}
                        </span>
                        <h3 className="mt-4 font-serif text-3xl leading-tight md:text-[40px] md:leading-[1.08]">
                            {eventSlots.length > 1
                                ? `${eventSlots.length} events. ${eventSlots.length} looks. One couple story.`
                                : "One event. One editorial couple portrait."}
                        </h3>
                        <p className="mt-4 text-[14px] leading-relaxed text-ivory/80">
                            {eventSlots.length > 1
                                ? "One editorial portrait for every ceremony you selected — each rendered with your actual faces at the venue and style you chose."
                                : "An editorial portrait for your ceremony, rendered with your actual faces at the venue and style you chose."}
                        </p>
                        {palette.length > 0 && (
                            <div className="mt-6">
                                <div className="text-[10px] uppercase tracking-[0.22em] text-ivory/50">Coordinated palette</div>
                                <div className="mt-3 flex flex-wrap gap-2">
                                    {palette.slice(0, 6).map((c) => <PaletteSwatch key={c} hex={c} />)}
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* Phase C.5 — per-event portraits now live inside <PaidReport>
               under Bride / Groom / Couple analysis sections. This gallery
               component only renders the hero couple portrait now. */}
        </section>
    );
}

export function WeddingLookGallery({ analysis }) {
    const gallery = analysis.gallery || {};
    const status = analysis.gallery_status; // "generating" | "ready" | "partial" | "failed" | undefined
    const generating = status === "generating" || (!status && analysis.paid);
    const palette = analysis?.decision?.color_palette || [];

    // Phase C branch — if the record has any of the new couple_* / backdrop_*
    // slot keys we render the new 5+2 layout. Legacy records fall through to
    // the old bride/groom event-by-event grid untouched.
    if (hasNewGallerySlots(gallery)) {
        return <WeddingLookGalleryPhaseC gallery={gallery} generating={generating} palette={palette} questionnaire={analysis?.questionnaire} />;
    }

    const hero = absGalleryUrl(gallery.couple_hero);
    const accessories = absGalleryUrl(gallery.accessories);

    return (
        <section className="space-y-8" data-testid="wedding-look-gallery">
            {/* Header card with hero */}
            <div className="overflow-hidden rounded-[28px] bg-charcoal text-ivory shadow-soft">
                <div className="grid gap-0 md:grid-cols-2">
                    <div className="relative aspect-[3/4] bg-charcoal md:aspect-auto md:min-h-[520px]">
                        {hero ? (
                            <img
                                src={hero}
                                alt="Wedding couple hero look"
                                className="absolute inset-0 h-full w-full object-contain object-center"
                                data-testid="gallery-couple-hero"
                            />
                        ) : (
                            <div className="absolute inset-0 flex flex-col items-center justify-center bg-ivory/5 text-ivory/40">
                                {generating ? (
                                    <>
                                        <Loader2 className="h-6 w-6 animate-spin" />
                                        <p className="mt-4 text-[11px] uppercase tracking-[0.22em]">Rendering your hero look…</p>
                                        <p className="mt-1 max-w-xs text-center text-[11px] text-ivory/30">Nano Banana is generating editorial portraits using your actual photos. ~60–90s.</p>
                                    </>
                                ) : (
                                    <>
                                        <ImageIcon className="h-6 w-6" />
                                        <p className="mt-3 text-[11px] uppercase tracking-[0.22em]">Hero unavailable</p>
                                    </>
                                )}
                            </div>
                        )}
                    </div>
                    <div className="flex flex-col justify-center p-8 md:p-10">
                        <span className="text-[11px] uppercase tracking-[0.25em] text-gold">Your wedding look</span>
                        <h3 className="mt-4 font-serif text-3xl leading-tight md:text-[40px] md:leading-[1.08]">
                            Calibrated to both of you.
                        </h3>
                        <p className="mt-4 text-[14px] leading-relaxed text-ivory/80">
                            Hero portrait + bride and groom looks for every wedding event — rendered with your actual faces, the palette below, and the venue lighting from your questionnaire.
                        </p>
                        {palette.length > 0 && (
                            <div className="mt-6">
                                <div className="text-[10px] uppercase tracking-[0.22em] text-ivory/50">Coordinated palette</div>
                                <div className="mt-3 flex flex-wrap gap-2">
                                    {palette.slice(0, 6).map((c) => <PaletteSwatch key={c} hex={c} />)}
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* Bride looks */}
            <div className="rounded-[28px] bg-white p-7 shadow-soft md:p-10" data-testid="gallery-bride-section">
                <div className="flex items-baseline justify-between">
                    <h4 className="font-serif text-2xl text-charcoal md:text-3xl">Bride · event-by-event</h4>
                    <span className="text-[11px] uppercase tracking-[0.22em] text-charcoal/40">4 looks</span>
                </div>
                <div className="mt-6 grid grid-cols-2 gap-3 sm:gap-4 md:grid-cols-4">
                    {BRIDE_SLOTS.map((s) => (
                        <GalleryTile key={s.k} url={absGalleryUrl(gallery[s.k])} label={s.label} generating={generating} />
                    ))}
                </div>
            </div>

            {/* Groom looks */}
            <div className="rounded-[28px] bg-white p-7 shadow-soft md:p-10" data-testid="gallery-groom-section">
                <div className="flex items-baseline justify-between">
                    <h4 className="font-serif text-2xl text-charcoal md:text-3xl">Groom · event-by-event</h4>
                    <span className="text-[11px] uppercase tracking-[0.22em] text-charcoal/40">4 looks</span>
                </div>
                <div className="mt-6 grid grid-cols-2 gap-3 sm:gap-4 md:grid-cols-4">
                    {GROOM_SLOTS.map((s) => (
                        <GalleryTile key={s.k} url={absGalleryUrl(gallery[s.k])} label={s.label} generating={generating} />
                    ))}
                </div>
            </div>

            {/* Accessories */}
            <div className="rounded-[28px] bg-white p-7 shadow-soft md:p-10" data-testid="gallery-accessories-section">
                <div className="flex items-baseline justify-between">
                    <h4 className="font-serif text-2xl text-charcoal md:text-3xl">Accessories matched to your palette</h4>
                    <span className="text-[11px] uppercase tracking-[0.22em] text-charcoal/40">Flat-lay</span>
                </div>
                <div className="mt-6 grid gap-6 md:grid-cols-12">
                    <div className="md:col-span-7">
                        {accessories ? (
                            <img src={accessories} alt="Accessories flat-lay" className="aspect-[5/4] w-full rounded-[18px] object-cover" data-testid="gallery-accessories" />
                        ) : (
                            <div className="flex aspect-[5/4] w-full flex-col items-center justify-center rounded-[18px] bg-champagne/40 text-charcoal/40">
                                {generating ? (
                                    <>
                                        <Loader2 className="h-5 w-5 animate-spin" />
                                        <p className="mt-3 text-[11px] uppercase tracking-[0.22em]">Rendering…</p>
                                    </>
                                ) : (
                                    <>
                                        <ImageIcon className="h-5 w-5" />
                                        <p className="mt-3 text-[11px] uppercase tracking-[0.22em]">Unavailable</p>
                                    </>
                                )}
                            </div>
                        )}
                    </div>
                    <ul className="space-y-3 text-[14px] text-charcoal/85 md:col-span-5">
                        {[
                            "Leather watch · brown or tan strap",
                            "Tan / brown leather belt",
                            "Polished oxfords + clean white sneakers",
                            "Kundan / polki earrings for the bride",
                            "Statement neckpiece tied to the main palette",
                            "Silk pocket square in palette accent",
                        ].map((t) => (
                            <li key={t} className="flex items-start gap-3">
                                <Check className="mt-0.5 h-4 w-4 flex-none text-gold" />
                                <span>{t}</span>
                            </li>
                        ))}
                    </ul>
                </div>
            </div>
        </section>
    );
}

// -----------------------------------------------------------------------------
// Static data for the Paid Unlock Showcase. Real /gen/ images so no new assets.
// -----------------------------------------------------------------------------
const SNEAK_TILES = [
    { src: "/gen/hero_couple.png",           label: "Couple hero portrait", locked: false, ratio: "aspect-[3/4]" },
    { src: "/gen/couple_coordination.png",   label: "Sangeet look",         locked: false, ratio: "aspect-[3/4]" },
    { src: "/gen/individual_portrait.png",   label: "Bride · reception",    locked: true,  ratio: "aspect-[3/4]" },
    { src: "/gen/individual_male.png",       label: "Groom · engagement",   locked: true,  ratio: "aspect-[3/4]" },
    { src: "/gen/hands_jewelry.png",         label: "Accessories flat-lay", locked: false, ratio: "aspect-[3/4]" },
    { src: "/gen/fabric_gold.png",           label: "Palette · moodboard",  locked: true,  ratio: "aspect-[3/4]" },
];

const CONTENT_TEASERS = [
    {
        icon: "Film",
        chapter: "Storyboard — Chapter 1",
        title: "Sunrise · The First Look",
        teaser: "As the first light spills over the temple courtyard, she turns — and for one impossibly still second the whole ceremony holds its breath. The palette here is warm gold on ivory, with a whisper of blush from the marigold garlands. Your outfits are chosen so that…",
    },
    {
        icon: "Palette",
        chapter: "Moodboard — Vibe words",
        title: "Cinematic · Rooted · Regal",
        teaser: "Your palette lives in the warm midtones — champagne (#c99a5b), oxblood, aged ivory. Below is the exact HEX + fabric weight mapping for every event, plus the two colours you must NEVER wear together with your skin combination because…",
    },
    {
        icon: "Camera",
        chapter: "Shot Planner — Must-have shots",
        title: "The 12 frames your album must have",
        teaser: "Every frame is a specific angle, a specific time-of-day, and a specific outfit combination. Miss even one and your album will feel like every other Instagram wedding. Frame #3 is the one that most photographers get wrong because…",
    },
];

const VALUE_STACK = [
    { icon: "Sparkles",  title: "5-module strategy report",    sub: "Storyboard · Moodboard · Shot Planner · Pose Guide · Coordination" },
    { icon: "ImageIcon", title: "10 editorial AI portraits",   sub: "Rendered with YOUR faces across every wedding event" },
    { icon: "Palette",   title: "HEX-precise colour palette",  sub: "Every outfit + decor colour, mapped to your skin tones" },
    { icon: "Camera",    title: "Photo outcome prediction",    sub: "What your final album will feel like — before you spend a rupee" },
    { icon: "Download",  title: "PDF export + WhatsApp share", sub: "Send to your photographer, decorator, family — instantly" },
];

// Icon resolver — lets the data arrays above stay declarative strings while
// still letting the bundler tree-shake lucide-react imports normally.
const ICON_MAP_TO_INIT = () => ({ Sparkles, ImageIcon, Download, Share2, MessageCircle, Lock, Unlock, Check, Shield, Film, Palette, Camera });
function _Icon({ name, className }) {
    const map = ICON_MAP_TO_INIT();
    const C = map[name];
    if (!C) return null;
    return <C className={className} />;
}


function SneakPeekGrid() {
    return (
        <div className="grid grid-cols-2 gap-3 md:grid-cols-3 md:gap-4" data-testid="paid-showcase-sneak-peek">
            {SNEAK_TILES.map((t, i) => (
                <div key={i} className="group relative overflow-hidden rounded-[18px] bg-champagne/30 shadow-soft ring-1 ring-charcoal/5">
                    <img
                        src={t.src}
                        alt={t.label}
                        className={`w-full object-cover ${t.ratio} transition duration-500 ${t.locked ? "blur-xl scale-110" : "group-hover:scale-105"}`}
                        loading="lazy"
                    />
                    {t.locked && (
                        <div className="absolute inset-0 flex items-center justify-center bg-gradient-to-t from-charcoal/70 via-charcoal/30 to-transparent">
                            <span className="inline-flex h-9 w-9 items-center justify-center rounded-full bg-charcoal/80 ring-1 ring-gold/40">
                                <Lock className="h-4 w-4 text-gold" />
                            </span>
                        </div>
                    )}
                    <div className="absolute inset-x-0 bottom-0 flex items-center justify-between gap-2 bg-gradient-to-t from-charcoal/85 to-transparent px-3 py-2 text-[10.5px] uppercase tracking-[0.14em] text-ivory">
                        <span className="truncate">{t.label}</span>
                        {!t.locked && (
                            <span className="rounded-full bg-gold/25 px-2 py-0.5 text-[9px] text-gold">Preview</span>
                        )}
                    </div>
                </div>
            ))}
        </div>
    );
}

function ContentTeasers() {
    return (
        <div className="grid gap-3 md:grid-cols-3 md:gap-4" data-testid="paid-showcase-content-teasers">
            {CONTENT_TEASERS.map((c, i) => (
                <div key={i} className="relative overflow-hidden rounded-[20px] bg-white p-5 shadow-soft ring-1 ring-charcoal/5">
                    <div className="flex items-center gap-2 text-gold">
                        <_Icon name={c.icon} className="h-3.5 w-3.5" />
                        <span className="text-[10px] uppercase tracking-[0.22em] text-charcoal/60">{c.chapter}</span>
                    </div>
                    <h4 className="mt-2 font-serif text-[19px] leading-tight text-charcoal">{c.title}</h4>
                    <div className="relative mt-3 h-24 overflow-hidden">
                        <p className="text-[13px] leading-relaxed text-ink" style={{ filter: "blur(3.5px)", userSelect: "none" }}>
                            {c.teaser}
                        </p>
                        <div className="pointer-events-none absolute inset-x-0 bottom-0 h-12 bg-gradient-to-t from-white via-white/85 to-transparent" />
                    </div>
                    <div className="mt-3 flex items-center gap-1.5 text-[10.5px] uppercase tracking-[0.18em] text-gold">
                        <Lock className="h-3 w-3" />
                        Unlock to read
                    </div>
                </div>
            ))}
        </div>
    );
}

function ValueStack() {
    return (
        <div className="grid gap-3 md:grid-cols-2" data-testid="paid-showcase-value-stack">
            {VALUE_STACK.map((v, i) => (
                <div key={i} className="flex items-start gap-3 rounded-[16px] bg-white p-4 shadow-soft ring-1 ring-charcoal/5">
                    <span className="inline-flex h-9 w-9 flex-none items-center justify-center rounded-full bg-gold/15 text-gold">
                        <_Icon name={v.icon} className="h-4 w-4" />
                    </span>
                    <div>
                        <div className="text-[13.5px] font-medium text-charcoal">{v.title}</div>
                        <div className="mt-0.5 text-[12px] leading-relaxed text-ink">{v.sub}</div>
                    </div>
                </div>
            ))}
        </div>
    );
}

function ComparisonStrip() {
    const rows = [
        ["See a complete example report",      true,  true],
        ["Personalised with your own photos",  false, true],
        ["Coordinated for your venue & colours", false, true],
        ["10 AI portraits of you two",         false, true],
        ["Photo outcome prediction",           false, true],
        ["PDF + WhatsApp share",               false, true],
        ["Personalised Story Studio concept",  false, true],
    ];
    return (
        <div className="overflow-hidden rounded-[20px] bg-white shadow-soft ring-1 ring-charcoal/5" data-testid="paid-showcase-comparison">
            <div className="grid grid-cols-[1fr_auto_auto] gap-x-6 border-b border-charcoal/5 px-5 py-3 text-[10.5px] uppercase tracking-[0.22em] text-charcoal/60 md:px-6">
                <span>What&apos;s included</span>
                <span className="text-center">Sample</span>
                <span className="text-center text-gold">Your full report</span>
            </div>
            {rows.map(([label, sample, full], i) => (
                <div key={i} className={`grid grid-cols-[1fr_auto_auto] items-center gap-x-6 px-5 py-2.5 text-[13.5px] md:px-6 ${i % 2 ? "bg-champagne/20" : ""}`}>
                    <span className="text-charcoal">{label}</span>
                    <span className="w-8 text-center">
                        {sample ? <Check className="mx-auto h-4 w-4 text-emerald-600" /> : <span className="text-charcoal/25">—</span>}
                    </span>
                    <span className="w-8 text-center">
                        {full ? <Check className="mx-auto h-4 w-4 text-gold" /> : <span className="text-charcoal/25">—</span>}
                    </span>
                </div>
            ))}
        </div>
    );
}

function MegaCTA({ onStartUnlock, ctaRef }) {
    const pricing = usePricing();
    const priceLabel = formatINR(pricing.current_paise);
    return (
        <div
            ref={ctaRef}
            className="relative overflow-hidden rounded-[28px] bg-charcoal p-8 text-ivory shadow-soft ring-1 ring-gold/20 md:p-12"
            data-testid="paid-showcase-mega-cta"
        >
            {/* subtle radial glow */}
            <div
                className="pointer-events-none absolute -top-24 right-0 h-96 w-96 rounded-full opacity-20 blur-3xl"
                style={{ background: "radial-gradient(circle, rgba(199,158,84,0.65) 0%, transparent 70%)" }}
                aria-hidden
            />
            <div className="relative flex flex-col gap-8 md:flex-row md:items-center md:justify-between">
                <div className="max-w-xl">
                    <div className="flex flex-wrap items-center gap-2">
                        <span className="inline-flex items-center gap-2 rounded-full bg-gold/15 px-3 py-1 text-[10.5px] uppercase tracking-[0.22em] text-gold">
                            <Sparkles className="h-3 w-3" />
                            Stop guessing your wedding look
                        </span>
                        <LaunchPill tone="light" testId="mega-cta-launch-pill" />
                    </div>
                    <h3 className="mt-5 font-serif text-3xl leading-[1.08] md:text-[42px]">
                        Unlock the full strategy — <span className="text-gold">rendered with your faces</span>.
                    </h3>
                    <p className="mt-4 text-[15px] leading-relaxed text-ivory/80">
                        The sample report shows the direction. Your <span className="text-ivory">full report</span> shows the exact plan — coordinated outfits, palette HEXes, 10 AI portraits of you two across every event, and a photo outcome prediction. Delivered in ~90 seconds.
                    </p>

                    <div className="mt-7">
                        <PriceLine tone="light" size="xl" showBadge={false} testId="mega-cta-price" />
                        <div className="mt-2 text-[13px] text-ivory/60">one-time · instant unlock · delivered in ~90 seconds</div>
                    </div>

                    <button
                        type="button"
                        onClick={onStartUnlock}
                        data-testid="mega-cta-button"
                        className="mt-7 inline-flex items-center justify-center gap-2 rounded-full bg-gold px-8 py-4 text-[14px] font-medium text-charcoal transition hover:bg-goldDark"
                    >
                        <Unlock className="h-4 w-4" />
                        Tell your story · {priceLabel}
                    </button>
                    <div className="mt-4 flex flex-wrap gap-x-5 gap-y-2 text-[12px] text-ivory/70">
                        <span className="inline-flex items-center gap-1.5">
                            <Shield className="h-3.5 w-3.5 text-gold" />
                            Secure payment via Razorpay
                        </span>
                        <span className="inline-flex items-center gap-1.5">
                            <MessageCircle className="h-3.5 w-3.5 text-gold" />
                            Delivered on WhatsApp + PDF
                        </span>
                        <span className="inline-flex items-center gap-1.5">
                            <Sparkles className="h-3.5 w-3.5 text-gold" />
                            No subscription
                        </span>
                    </div>
                </div>

                <div className="rounded-[20px] border border-ivory/10 bg-ivory/[0.03] p-5 md:w-72 md:flex-none">
                    <div className="text-[10px] uppercase tracking-[0.22em] text-gold">You&apos;ll receive</div>
                    <ul className="mt-3 space-y-2.5 text-[13px] text-ivory/85">
                        <li className="flex items-start gap-2"><Check className="mt-0.5 h-3.5 w-3.5 flex-none text-gold" /> 5-module strategy PDF</li>
                        <li className="flex items-start gap-2"><Check className="mt-0.5 h-3.5 w-3.5 flex-none text-gold" /> 10 editorial AI portraits</li>
                        <li className="flex items-start gap-2"><Check className="mt-0.5 h-3.5 w-3.5 flex-none text-gold" /> Full HEX palette + fabric guide</li>
                        <li className="flex items-start gap-2"><Check className="mt-0.5 h-3.5 w-3.5 flex-none text-gold" /> Coordination for every event</li>
                        <li className="flex items-start gap-2"><Check className="mt-0.5 h-3.5 w-3.5 flex-none text-gold" /> Personalised Story Studio concept</li>
                        <li className="flex items-start gap-2"><Check className="mt-0.5 h-3.5 w-3.5 flex-none text-gold" /> Instant WhatsApp share link</li>
                    </ul>
                    <div className="mt-4 border-t border-ivory/10 pt-3 text-[10.5px] italic text-ivory/50">
                        Delivered as a beautifully-designed private link — yours to keep forever.
                    </div>
                </div>
            </div>
        </div>
    );
}

// ---------------------------------------------------------------------------
// Event-Packs pricing section (Feb 2026 patch — replaces the old flat price)
// ---------------------------------------------------------------------------
function formatRupees(paise) {
    const n = Math.round((paise || 0) / 100);
    return "₹" + n.toLocaleString("en-IN");
}

function EventPacksPricing({ analysis, onPickPack, unlockRef, preselectedPack }) {
    const pricing = usePricing();
    const packs = Array.isArray(pricing?.event_packs) ? pricing.event_packs : [];
    const individual = pricing?.individual_event_paise || 39900;
    const q = analysis?.questionnaire || {};
    const availableEvents = Array.isArray(q.events_brief) ? q.events_brief.length : 0;
    if (packs.length === 0) return null;
    const HIGHLIGHT = { 3: "POPULAR", 5: "BEST VALUE" };

    return (
        <section
            ref={unlockRef}
            className="rounded-[28px] bg-white p-6 shadow-soft md:p-10"
            data-testid="event-packs-pricing"
        >
            <div className="text-center">
                <span className="eyebrow" data-testid="event-packs-eyebrow">
                    {preselectedPack ? "You're almost there." : "Choose your event pack."}
                </span>
                <h2 className="mt-4 font-serif text-3xl leading-tight text-charcoal md:text-[42px] md:leading-[1.12]" data-testid="event-packs-headline">
                    {preselectedPack
                        ? `Confirm your ${preselectedPack}-event pack to continue.`
                        : "See your wedding the way it could actually look."}
                </h2>
                <p className="mx-auto mt-4 max-w-2xl text-[15px] text-ink">
                    See how your complete wedding style comes together across every event — bride, groom, couple and decor, all coordinated.
                </p>
            </div>
            <div className="mt-10 text-center">
                <div className="eyebrow">Build your wedding vision</div>
                <p className="mt-2 text-[13px] text-ink">Choose how many wedding events you want to visualize.</p>
            </div>
            <div className="mt-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-5" data-testid="event-packs-grid">
                {packs.map((p) => {
                    const size = p.events;
                    const priceRs = Math.round(p.price_paise / 100);
                    const individualTotalRs = Math.round((individual * size) / 100);
                    const saveRs = individualTotalRs - priceRs;
                    const badge = HIGHLIGHT[size];
                    const featured = size === 5;
                    const disabled = availableEvents > 0 && size > availableEvents;
                    const isPreselected = preselectedPack === size;
                    return (
                        <div
                            key={size}
                            className={`relative flex flex-col rounded-[22px] p-5 ring-1 transition
                              ${featured
                                    ? "bg-charcoal text-ivory ring-gold/40 shadow-soft"
                                    : "bg-champagne/30 text-charcoal ring-charcoal/5 hover:ring-charcoal/15"}
                              ${isPreselected ? "ring-2 ring-gold shadow-soft" : ""}`}
                            data-testid={`event-pack-${size}`}
                        >
                            {isPreselected && (
                                <span
                                    className="absolute -top-3 right-5 rounded-full bg-gold px-3 py-1 text-[10px] font-semibold uppercase tracking-[0.18em] text-charcoal"
                                    data-testid={`event-pack-${size}-preselected-badge`}
                                >
                                    Your pick
                                </span>
                            )}
                            {badge && !isPreselected && (
                                <span
                                    className={`absolute -top-3 left-5 rounded-full px-3 py-1 text-[10px] font-semibold uppercase tracking-[0.18em]
                                      ${featured ? "bg-gold text-charcoal" : "bg-charcoal text-ivory"}`}
                                    data-testid={`event-pack-${size}-badge`}
                                >
                                    {badge}
                                </span>
                            )}
                            <div className={`text-[10.5px] uppercase tracking-[0.22em] ${featured ? "text-gold" : "text-charcoal/60"}`}>
                                {size} {size === 1 ? "event" : "events"}
                            </div>
                            <div className={`mt-2 font-serif text-4xl leading-none ${featured ? "text-ivory" : "text-charcoal"}`} data-testid={`event-pack-${size}-price`}>
                                {formatRupees(p.price_paise)}
                            </div>
                            <p className={`mt-3 text-[13px] leading-snug ${featured ? "text-ivory/80" : "text-ink"}`}>
                                {size === 1 && "Perfect for trying one complete wedding look."}
                                {size === 2 && "Build two coordinated wedding looks."}
                                {size === 3 && "Three coordinated events — most couples pick this."}
                                {size === 4 && "Four events, fully styled and visualised."}
                                {size === 5 && "Every event — the complete wedding style experience."}
                            </p>
                            <ul className={`mt-3 space-y-1.5 text-[12.5px] leading-snug ${featured ? "text-ivory/85" : "text-charcoal/80"}`}>
                                <li>· {p.images} personalised visualisations</li>
                                <li>· Bride + groom + couple styling</li>
                                <li>· Decor / backdrop visualisation</li>
                                <li>· Coordinated colour palette</li>
                            </ul>
                            {saveRs > 0 && (
                                <div className={`mt-3 text-[11.5px] ${featured ? "text-gold" : "text-emerald-700"}`} data-testid={`event-pack-${size}-savings`}>
                                    Save ₹{saveRs.toLocaleString("en-IN")} vs buying {size} individually
                                </div>
                            )}
                            <div className="mt-auto pt-5">
                                <button
                                    type="button"
                                    onClick={() => !disabled && onPickPack(size)}
                                    disabled={disabled}
                                    data-testid={`event-pack-${size}-cta`}
                                    className={`w-full rounded-full px-4 py-3 text-[13px] font-medium transition
                                        ${featured
                                            ? "bg-gold text-charcoal hover:bg-goldDark"
                                            : "bg-charcoal text-ivory hover:bg-charcoal/90"}
                                        disabled:cursor-not-allowed disabled:opacity-40`}
                                >
                                    {size === 5 ? "Visualize all 5 events" : `Visualize ${size} ${size === 1 ? "event" : "events"}`}
                                </button>
                                {disabled && (
                                    <p className="mt-2 text-center text-[11px] text-charcoal/60">
                                        You've briefed {availableEvents} event{availableEvents === 1 ? "" : "s"}. Add more to unlock this pack.
                                    </p>
                                )}
                            </div>
                        </div>
                    );
                })}
            </div>
            <p className="mt-8 text-center text-[12px] text-ink/70">
                All prices are final · Secure payment via Razorpay · No surprise charges at checkout.
            </p>
        </section>
    );
}



function PaidUnlockShowcase({ onStartUnlock, unlockRef }) {
    return (
        <div className="space-y-8" data-testid="paid-unlock-showcase">
            {/* Section 1 — Sneak-peek gallery */}            <div className="rounded-[28px] bg-white p-6 shadow-soft md:p-8">
                <div className="flex flex-wrap items-end justify-between gap-3">
                    <div>
                        <span className="eyebrow">Full report · sneak peek</span>
                        <h3 className="mt-3 font-serif text-3xl leading-tight text-charcoal md:text-[36px]">
                            10 editorial portraits — <span className="text-gold">rendered with your faces</span>.
                        </h3>
                        <p className="mt-2 max-w-2xl text-[14px] text-ink">
                            Below is a preview of what our AI generates for paid couples. Yours will feature the bride and groom from the photos you uploaded — across every event of the wedding.
                        </p>
                    </div>
                    <span className="rounded-full bg-champagne px-3 py-1 text-[11px] tracking-[0.14em] text-charcoal/70">
                        3 shown · 7 more locked
                    </span>
                </div>
                <div className="mt-6">
                    <SneakPeekGrid />
                </div>
            </div>

            {/* Section 2 — Content teasers (blurred) */}
            <div>
                <div className="mb-4 flex items-center justify-between gap-3">
                    <div>
                        <span className="eyebrow">A peek inside the writing</span>
                        <h3 className="mt-2 font-serif text-2xl leading-tight text-charcoal md:text-3xl">
                            Every chapter is written for the two of you.
                        </h3>
                    </div>
                </div>
                <ContentTeasers />
            </div>

            {/* Section 3 — Value stack */}
            <div>
                <div className="mb-4">
                    <span className="eyebrow">What you unlock</span>
                    <h3 className="mt-2 font-serif text-2xl leading-tight text-charcoal md:text-3xl">
                        Five outputs. One coordinated wedding.
                    </h3>
                </div>
                <ValueStack />
            </div>

            {/* Section 4 — Sample vs Full comparison */}
            <div>
                <div className="mb-4">
                    <span className="eyebrow">Sample vs full report</span>
                    <h3 className="mt-2 font-serif text-2xl leading-tight text-charcoal md:text-3xl">
                        The delta, in one glance.
                    </h3>
                </div>
                <ComparisonStrip />
            </div>

            {/* Section 5 — Mega CTA */}
            <MegaCTA onStartUnlock={onStartUnlock} ctaRef={unlockRef} />
        </div>
    );
}


// ---------- Share + Download actions (PDF + WhatsApp) ----------
const SHARE_BACKEND = process.env.REACT_APP_BACKEND_URL || "";

function buildWhatsAppUrl(resultUrl, names) {
    const pitch = names
        ? `${names} just got their wedding look strategy on Vowze 💍 — check it out:`
        : "Just got my Vowze wedding look strategy 💍 — check it out:";
    const text = encodeURIComponent(`${pitch}\n${resultUrl}`);
    return `https://wa.me/?text=${text}`;
}

function useShareActions(analysis) {
    const [downloading, setDownloading] = useState(false);
    const resultUrl = typeof window !== "undefined"
        ? `${window.location.origin}/result/${analysis?.id}`
        : "";
    const names = analysis?.name || "";

    const downloadPdf = useCallback(async () => {
        if (!analysis?.id) return;
        setDownloading(true);
        const toastId = toast.loading("Preparing your PDF (~30–60s)…");
        try {
            const resp = await fetch(`${SHARE_BACKEND}/api/analysis/${analysis.id}/pdf`, {
                method: "GET",
                credentials: "omit",
            });
            if (!resp.ok) {
                let detail = "PDF render failed.";
                try {
                    const body = await resp.json();
                    detail = body?.detail || detail;
                } catch (_) { /* noop */ }
                throw new Error(detail);
            }
            const blob = await resp.blob();
            const url = URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url;
            const filename = `Vowze_${(names || "Wedding").replace(/[^A-Za-z0-9_-]+/g, "_")}.pdf`;
            a.download = filename;
            document.body.appendChild(a);
            a.click();
            a.remove();
            setTimeout(() => URL.revokeObjectURL(url), 5000);
            toast.success("Your PDF is downloading.", { id: toastId });
        } catch (err) {
            toast.error(err?.message || "PDF download failed.", { id: toastId });
        } finally {
            setDownloading(false);
        }
    }, [analysis?.id, names]);

    const shareWhatsApp = useCallback(() => {
        if (!resultUrl) return;
        const wa = buildWhatsAppUrl(resultUrl, names);
        window.open(wa, "_blank", "noopener,noreferrer");
    }, [resultUrl, names]);

    return { downloadPdf, shareWhatsApp, downloading };
}

function ShareActionsInline({ analysis }) {
    const { downloadPdf, shareWhatsApp, downloading } = useShareActions(analysis);
    return (
        <div
            className="flex flex-col gap-3 rounded-[24px] bg-white p-5 shadow-soft ring-1 ring-charcoal/5 md:flex-row md:items-center md:justify-between md:p-6"
            data-testid="share-actions-inline"
        >
            <div className="flex items-start gap-3">
                <span className="inline-flex h-9 w-9 flex-none items-center justify-center rounded-full bg-champagne">
                    <Share2 className="h-4 w-4 text-charcoal" />
                </span>
                <div>
                    <div className="text-[11px] uppercase tracking-[0.24em] text-gold">Take it with you</div>
                    <div className="text-[14.5px] font-medium text-charcoal">Download or share your wedding plan</div>
                    <div className="text-[12.5px] text-ink">Save as a high-res PDF or send a link via WhatsApp.</div>
                </div>
            </div>
            <div className="flex flex-col gap-2 sm:flex-row">
                <button
                    type="button"
                    onClick={downloadPdf}
                    disabled={downloading}
                    data-testid="share-download-pdf"
                    className="inline-flex items-center justify-center gap-2 rounded-full bg-charcoal px-4 py-2.5 text-[12.5px] text-ivory transition hover:bg-charcoal/90 disabled:opacity-60"
                >
                    {downloading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
                    {downloading ? "Preparing…" : "Download PDF"}
                </button>
                <button
                    type="button"
                    onClick={shareWhatsApp}
                    data-testid="share-whatsapp"
                    className="inline-flex items-center justify-center gap-2 rounded-full bg-[#25D366] px-4 py-2.5 text-[12.5px] font-medium text-white transition hover:brightness-95"
                >
                    <MessageCircle className="h-4 w-4" />
                    Share on WhatsApp
                </button>
            </div>
        </div>
    );
}

/** "Save my Vowze card" — downloads the 1080-sq Instagram palette card and
 * (optionally) pushes it to WhatsApp. Only rendered on paid records. */
function PaletteCardActions({ analysis }) {
    const [previewUrl, setPreviewUrl] = useState(null);
    const [loading, setLoading] = useState(false);
    const [sending, setSending] = useState(false);
    const backend = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");

    const ensureUrl = useCallback(async () => {
        if (previewUrl) return previewUrl;
        setLoading(true);
        try {
            const res = await fetch(`${backend}/api/analysis/${analysis.id}/palette-card`, { credentials: "omit" });
            const data = await res.json();
            if (!res.ok) throw new Error(data?.detail || "Could not load palette card.");
            const abs = data.url.startsWith("http") ? data.url : `${backend}${data.url}`;
            setPreviewUrl(abs);
            return abs;
        } catch (e) {
            toast.error(e?.message || "Could not load palette card.");
            return null;
        } finally {
            setLoading(false);
        }
    }, [previewUrl, analysis.id, backend]);

    useEffect(() => { ensureUrl(); }, [ensureUrl]);

    const download = useCallback(async () => {
        const url = await ensureUrl();
        if (!url) return;
        const a = document.createElement("a");
        a.href = url;
        a.download = "vowze-palette-card.jpg";
        document.body.appendChild(a);
        a.click();
        a.remove();
        toast.success("Saved your Vowze card.");
    }, [ensureUrl]);

    const sendWhatsApp = useCallback(async () => {
        setSending(true);
        try {
            const res = await fetch(`${backend}/api/analysis/${analysis.id}/palette-card/send-whatsapp`, {
                method: "POST",
                credentials: "omit",
            });
            const data = await res.json();
            if (!res.ok) throw new Error(data?.detail || "WhatsApp send failed");
            toast.success("Sent to your WhatsApp.");
        } catch (e) {
            toast.error(e?.message || "WhatsApp send failed.");
        } finally {
            setSending(false);
        }
    }, [analysis.id, backend]);

    return (
        <div
            className="grid gap-6 rounded-[24px] bg-charcoal p-6 text-ivory shadow-soft md:grid-cols-[minmax(0,320px)_1fr] md:items-center md:p-8"
            data-testid="palette-card-actions"
        >
            <div className="mx-auto w-full max-w-[280px] overflow-hidden rounded-[18px] bg-ivory/5 ring-1 ring-gold/25">
                {loading && (
                    <div className="flex aspect-square items-center justify-center">
                        <Loader2 className="h-5 w-5 animate-spin text-gold" />
                    </div>
                )}
                {!loading && previewUrl && (
                    <img
                        src={previewUrl}
                        alt="Your Vowze palette card"
                        className="block aspect-square w-full object-cover"
                        data-testid="palette-card-preview"
                    />
                )}
                {!loading && !previewUrl && (
                    <div className="flex aspect-square items-center justify-center text-[12px] text-ivory/60">
                        Card unavailable
                    </div>
                )}
            </div>
            <div>
                <div className="flex items-center gap-2 text-gold">
                    <Palette className="h-4 w-4" />
                    <span className="text-[10.5px] uppercase tracking-[0.24em]">Instagram-ready palette card</span>
                </div>
                <h3 className="mt-2 font-serif text-2xl leading-tight md:text-[26px]">
                    A 1080-square you can post the week before.
                </h3>
                <p className="mt-2 text-[13.5px] text-ivory/75">
                    Your couple palette, hex codes and names — designed to look Vowze on any grid.
                    Save it, share it, and let your guests know the aesthetic is intentional.
                </p>
                <div className="mt-5 flex flex-wrap gap-2">
                    <button
                        type="button"
                        onClick={download}
                        disabled={loading}
                        data-testid="palette-card-download"
                        className="inline-flex items-center justify-center gap-2 rounded-full bg-gold px-5 py-2.5 text-[13px] font-medium text-charcoal transition hover:bg-goldDark disabled:opacity-50"
                    >
                        <Download className="h-4 w-4" /> Save my Vowze card
                    </button>
                    <button
                        type="button"
                        onClick={sendWhatsApp}
                        disabled={sending}
                        data-testid="palette-card-whatsapp"
                        className="inline-flex items-center justify-center gap-2 rounded-full bg-[#25D366] px-5 py-2.5 text-[13px] font-medium text-white transition hover:brightness-95 disabled:opacity-50"
                    >
                        {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <MessageCircle className="h-4 w-4" />}
                        {sending ? "Sending…" : "Send to WhatsApp"}
                    </button>
                </div>
            </div>
        </div>
    );
}

function ShareActionsSticky({ analysis }) {
    const { downloadPdf, shareWhatsApp, downloading } = useShareActions(analysis);
    return (
        <div
            className="sticky top-20 z-30 ml-auto flex w-fit flex-wrap items-center gap-2 rounded-full bg-white/85 px-2 py-2 shadow-soft ring-1 ring-charcoal/10 backdrop-blur"
            data-testid="share-actions-sticky"
        >
            <button
                type="button"
                onClick={downloadPdf}
                disabled={downloading}
                data-testid="share-download-pdf-sticky"
                title="Download PDF"
                className="inline-flex items-center gap-2 rounded-full bg-charcoal px-3.5 py-2 text-[12px] text-ivory transition hover:bg-charcoal/90 disabled:opacity-60"
            >
                {downloading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Download className="h-3.5 w-3.5" />}
                <span className="hidden sm:inline">PDF</span>
            </button>
            <button
                type="button"
                onClick={shareWhatsApp}
                data-testid="share-whatsapp-sticky"
                title="Share on WhatsApp"
                className="inline-flex items-center gap-2 rounded-full bg-[#25D366] px-3.5 py-2 text-[12px] font-medium text-white transition hover:brightness-95"
            >
                <MessageCircle className="h-3.5 w-3.5" />
                <span className="hidden sm:inline">WhatsApp</span>
            </button>
        </div>
    );
}
const PHASE_1_MESSAGES = [
    "Reading the bride's skin tone…",
    "Reading the groom's skin tone…",
    "Calibrating couple-level visual harmony…",
    "Mapping your venue lighting to fabric reflectance…",
    "Drafting your event-by-event outfit plan…",
    "Locking in the coordinated palette…",
    "Writing the contrast and balance notes…",
    "Predicting how you'll read on camera…",
];

const PHASE_2_MESSAGES = [
    "Rendering your couple hero portrait…",
    "Styling the bride's engagement lehenga…",
    "Adding fresh mogras for the haldi morning…",
    "Sculpting the sangeet sharara silhouette…",
    "Tailoring the bride's reception saree drape…",
    "Cutting the groom's engagement bandhgala…",
    "Softening the groom's haldi kurta linen…",
    "Embroidering the sangeet jacket details…",
    "Polishing the groom's reception velvet…",
    "Composing the accessories flat-lay…",
];

const PHASE_3_MESSAGES = [
    "Blocking your wedding storyboard scene by scene…",
    "Framing the ring-exchange sequence…",
    "Choreographing your haldi playfulness…",
    "Sketching the mehendi hand close-ups…",
    "Lighting your sangeet performance beats…",
    "Positioning the couple at the mandapam…",
    "Composing the sunset walk-away frames…",
    "Rendering 16 pose variations per scene…",
    "Stitching each 4×4 storyboard grid…",
    "Signing off with your final wedding portrait…",
];

const PHASE_QA_MESSAGES = [
    "Checking your colour palette stays consistent across every look…",
    "Making sure bride, groom and couple styling are coordinated…",
    "Confirming your wedding style comes through in every visual…",
    "Reviewing each portrait for clarity and consistency…",
    "Cross-checking your report text against the final visuals…",
    "Running the last quality pass before your reveal…",
];

// Terminal pipeline states — a paid report never displays from these; a
// friendly failure card takes over instead (see PipelineFailureState).
const FAILURE_STAGES = ["NEEDS_BETTER_INPUT", "STYLING_FAILED", "GENERATION_FAILED", "REPORT_QA_FAILED"];

function ProgressRing({ percent }) {
    const r = 54;
    const c = 2 * Math.PI * r;
    const offset = c - (Math.min(100, Math.max(0, percent)) / 100) * c;
    return (
        <svg width="128" height="128" viewBox="0 0 128 128" className="-rotate-90">
            <circle cx="64" cy="64" r={r} fill="none" stroke="rgba(255,255,255,0.08)" strokeWidth="6" />
            <circle
                cx="64" cy="64" r={r} fill="none"
                stroke="url(#progressGold)"
                strokeWidth="6"
                strokeLinecap="round"
                strokeDasharray={c}
                strokeDashoffset={offset}
                style={{ transition: "stroke-dashoffset 600ms cubic-bezier(.2,.7,.2,1)" }}
            />
            <defs>
                <linearGradient id="progressGold" x1="0" y1="0" x2="1" y2="1">
                    <stop offset="0%" stopColor="#E8C896" />
                    <stop offset="100%" stopColor="#C19A5B" />
                </linearGradient>
            </defs>
        </svg>
    );
}

function UnlockProgress({ analysis }) {
    const galleryDone = ["ready", "partial", "failed"].includes(analysis?.gallery_status);
    const storyboardDone = ["ready", "partial", "failed"].includes(analysis?.storyboard_status);
    const unlocking = analysis?.unlock_status === "unlocking";
    const reportReady = Boolean(analysis?.report_ready);
    const pipelineFailed = FAILURE_STAGES.includes(analysis?.pipeline_status);
    const inPhase2 = Boolean(analysis?.paid && analysis?.gallery_status === "generating");
    const inPhase3 = Boolean(analysis?.paid && galleryDone && analysis?.storyboard_status === "generating");
    // Final quality-gate pass: gallery is technically done but the report
    // hasn't cleared REPORT_QA yet — nothing is shown to the customer until it does.
    const inPhaseQA = Boolean(analysis?.paid && galleryDone && storyboardDone && !reportReady && !pipelineFailed);
    // Safety net: paid but not yet in any of the specific phases above (e.g. the
    // brief window right after payment before gallery_status is first written,
    // or an interrupted/stalled record) — never leave the customer looking at a
    // blank page while report_ready is still false.
    const inFallback = Boolean(analysis?.paid && !reportReady && !pipelineFailed && !inPhase2 && !inPhase3 && !inPhaseQA);
    // We stay visible until the whole pipeline is READY (never on payment/generation alone).
    const visible = !reportReady && !pipelineFailed && (unlocking || inPhase2 || inPhase3 || inPhaseQA || inFallback);

    // Elapsed tracker (re-renders every second)
    const [now, setNow] = useState(Date.now());
    useEffect(() => {
        if (!visible) return;
        const t = setInterval(() => setNow(Date.now()), 1000);
        return () => clearInterval(t);
    }, [visible]);

    // Rotating messages
    const [msgIdx, setMsgIdx] = useState(0);
    useEffect(() => {
        if (!visible) return;
        const t = setInterval(() => setMsgIdx((i) => i + 1), 2800);
        return () => clearInterval(t);
    }, [visible]);

    if (!visible) return null;

    const startedAtIso = analysis?.unlock_started_at;
    const startedAt = startedAtIso ? new Date(startedAtIso).getTime() : now - 1000;
    const elapsedMs = Math.max(0, now - startedAt);
    const elapsedSec = Math.round(elapsedMs / 1000);

    // ----- 100%-accurate progress -----
    // Total "units" to complete = 1 (Vowze text) + expected_gallery_slots.
    // Progress only advances when a real backend milestone lands. No
    // wallclock micro-stepping — a stale bar is honest.
    const galleryObj = analysis?.gallery || {};
    const galleryImageCount = Object.values(galleryObj).filter((v) => v).length;
    // Prefer server-declared expected count; else infer from what we have
    // (once at least one slot has landed) so the bar has the correct target
    // from second 0 of Phase 2.
    const expectedFromServer = analysis?.expected_gallery_slots;
    const galleryTarget = expectedFromServer
        ? Number(expectedFromServer)
        : hasNewGallerySlots(galleryObj) ? 7 : (galleryImageCount || 7);
    const totalUnits = 1 + galleryTarget;         // 1 (text) + N (images)
    const doneUnits = (analysis?.paid ? 1 : 0) + galleryImageCount;

    // Observed avg per gallery slot from the timestamps the backend streams
    // (unlock_slot_timestamps: { slot: iso_ts, ... }). Falls back to the wall
    // clock between gallery_started_at and now if no slots have landed yet.
    const slotTimestamps = analysis?.unlock_slot_timestamps || {};
    const slotTsList = Object.values(slotTimestamps)
        .map((s) => (typeof s === "string" ? new Date(s).getTime() : 0))
        .filter((n) => Number.isFinite(n) && n > 0)
        .sort((a, b) => a - b);
    const galleryStartMs = analysis?.gallery_started_at
        ? new Date(analysis.gallery_started_at).getTime()
        : null;
    let avgSlotMs;
    if (slotTsList.length >= 2) {
        // Real observed cadence — avg gap between consecutive slot completions
        avgSlotMs = (slotTsList[slotTsList.length - 1] - slotTsList[0]) / (slotTsList.length - 1);
    } else if (slotTsList.length === 1 && galleryStartMs) {
        avgSlotMs = slotTsList[0] - galleryStartMs;
    } else {
        avgSlotMs = null;   // unknown until first slot lands
    }

    let percent;
    let phaseLabel;
    let stepLabel;
    let etaSec;
    let messages;

    if (!analysis?.paid) {
        // Phase 1: Vowze text (~80s). We do NOT know how far Claude is —
        // show a fixed 15% "writing" state so the bar is honest. It only
        // jumps when the report actually lands.
        percent = 15;
        phaseLabel = "Step 1 of 2 · Writing your strategy";
        stepLabel = "Vowze is reading your photos and authoring the report.";
        etaSec = null;
        messages = PHASE_1_MESSAGES;
    } else if (inPhase2) {
        // Phase 2: gallery (unit-accurate).
        // Clamp with the 15% phase-1 floor so the bar never regresses when Claude's
        // text lands on multi-event records (1/(1+4N) is < 15% for N >= 2).
        percent = Math.min(99, Math.max(15, (doneUnits / totalUnits) * 100));
        const remainingSlots = Math.max(0, galleryTarget - galleryImageCount);
        etaSec = avgSlotMs
            ? Math.max(1, Math.round((remainingSlots * avgSlotMs) / 1000))
            : null;
        phaseLabel = `Step 2 of 2 · Rendering your gallery (${galleryImageCount}/${galleryTarget})`;
        stepLabel = "Nano Banana is generating editorial portraits with your actual faces.";
        messages = PHASE_2_MESSAGES;
    } else if (inPhase3) {
        // Legacy storyboard phase (retired for new records; still supported).
        const storyboardProgress = analysis?.storyboard_progress || {};
        const storyboardCount = Object.values(storyboardProgress).filter((v) => v).length;
        const storyboardTarget = Math.max(1, Array.isArray(analysis?.storyboard) ? analysis.storyboard.length : 9);
        const sbRatio = Math.min(1, storyboardCount / storyboardTarget);
        percent = Math.min(99, 90 + sbRatio * 9);   // storyboard is now optional polish
        phaseLabel = `Finishing your walkthrough (${storyboardCount}/${storyboardTarget})`;
        stepLabel = "Composing storyboard collages.";
        etaSec = null;
        messages = PHASE_3_MESSAGES;
    } else if (inPhaseQA) {
        percent = 97;
        phaseLabel = "Step 3 of 3 · Final quality checks";
        stepLabel = "Confirming your report and visuals are fully coordinated before your reveal.";
        etaSec = 15;
        messages = PHASE_QA_MESSAGES;
    } else if (inFallback) {
        percent = 92;
        phaseLabel = "Finishing up";
        stepLabel = "Vowze is putting the last touches on your wedding vision.";
        etaSec = null;
        messages = PHASE_QA_MESSAGES;
    } else {
        return null;
    }

    const message = messages[msgIdx % messages.length];

    return (
        <div className="relative overflow-hidden rounded-[28px] bg-charcoal text-ivory shadow-soft" data-testid="unlock-progress">
            {/* Ambient gold sheen */}
            <div className="pointer-events-none absolute inset-0 -z-10">
                <div className="absolute -left-20 -top-20 h-72 w-72 rounded-full bg-gold/20 blur-3xl" />
                <div className="absolute -right-24 -bottom-24 h-72 w-72 rounded-full bg-gold/10 blur-3xl" />
            </div>

            <div className="grid items-center gap-8 p-8 md:grid-cols-12 md:gap-10 md:p-12">
                {/* Ring + ETA */}
                <div className="flex items-center justify-center md:col-span-4">
                    <div className="relative">
                        <ProgressRing percent={percent} />
                        <div className="absolute inset-0 flex flex-col items-center justify-center">
                            <span className="font-serif text-3xl text-ivory" data-testid="progress-percent">
                                {Math.round(percent)}<span className="text-base text-ivory/60">%</span>
                            </span>
                            <span className="mt-1 text-[10px] uppercase tracking-[0.22em] text-ivory/55" data-testid="progress-eta">
                                {etaSec === null ? "Calculating…" : `~${_fmtEta(etaSec)} left`}
                            </span>
                        </div>
                    </div>
                </div>

                {/* Status block */}
                <div className="md:col-span-8">
                    <div className="flex items-center gap-2 text-gold">
                        <Sparkles className="h-3.5 w-3.5" />
                        <span className="text-[10px] uppercase tracking-[0.28em]" data-testid="progress-phase">
                            {phaseLabel}
                        </span>
                    </div>
                    <h3 className="mt-3 font-serif text-2xl leading-tight text-ivory md:text-3xl">
                        Building your wedding look…
                    </h3>
                    <p className="mt-2 text-[13px] text-ivory/75">{stepLabel}</p>

                    {/* Rolling micro-message */}
                    <div className="mt-5 overflow-hidden rounded-full border border-ivory/10 bg-ivory/5 px-4 py-2.5">
                        <div key={msgIdx} className="rolling-msg flex items-center gap-2 text-[12.5px] text-ivory/85">
                            <Loader2 className="h-3.5 w-3.5 flex-none animate-spin text-gold" />
                            <span data-testid="progress-rolling-msg">{message}</span>
                        </div>
                    </div>

                    {/* Slot dots — accurate count matches expected_gallery_slots */}
                    {inPhase2 && (
                        <div className="mt-5 flex flex-wrap gap-1.5" data-testid="progress-slot-dots">
                            {Array.from({ length: galleryTarget }).map((_, i) => (
                                <span
                                    key={i}
                                    className={`h-1.5 w-6 rounded-full transition-colors duration-500 ${
                                        i < galleryImageCount
                                            ? "bg-gold"
                                            : "bg-ivory/15"
                                    }`}
                                />
                            ))}
                        </div>
                    )}

                    <p className="mt-4 text-[10px] uppercase tracking-[0.22em] text-ivory/40">
                        <span data-testid="progress-elapsed">{_fmtElapsed(elapsedSec)} elapsed</span> · You can keep this tab open in the background.
                    </p>
                </div>
            </div>
        </div>
    );
}

// mm:ss for the elapsed timer. Deterministic — no fake smoothing.
function _fmtElapsed(sec) {
    const s = Math.max(0, Math.floor(sec));
    const m = Math.floor(s / 60);
    const rem = s % 60;
    return `${m}:${String(rem).padStart(2, "0")}`;
}
// Short ETA — "45s" if <60s, "1m 05s" otherwise.
function _fmtEta(sec) {
    if (sec < 60) return `${sec}s`;
    const m = Math.floor(sec / 60);
    const rem = sec % 60;
    return `${m}m ${String(rem).padStart(2, "0")}s`;
}

/** Friendly, non-technical state shown when the quality gate can't complete
 * the paid report. Never exposes internal model/QA details to the customer. */
function PipelineFailureState({ analysis }) {
    const needsInput = analysis?.pipeline_status === "NEEDS_BETTER_INPUT";
    return (
        <div className="rounded-[28px] bg-white p-8 text-center shadow-soft md:p-12" data-testid="pipeline-failure-state">
            <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-champagne/70">
                <Sparkles className="h-6 w-6 text-gold" />
            </div>
            <h3 className="mt-6 font-serif text-2xl leading-snug text-charcoal md:text-3xl">
                We're still perfecting your wedding vision.
            </h3>
            <p className="mx-auto mt-4 max-w-md text-[15px] leading-relaxed text-ink">
                {needsInput
                    ? "We need a clearer photo of the bride and groom to build your full styling report. Our team will reach out to help you re-upload — your payment is safe and won't be charged again."
                    : "This one is taking a little longer than usual to pass our final quality checks. Our team has been notified and will make sure your full report reaches you shortly — your payment is safe and won't be charged again."}
            </p>
            <p className="mt-6 text-xs uppercase tracking-[0.2em] text-charcoal/50">
                Reference ID: {analysis?.id}
            </p>
        </div>
    );
}

function UnlockPanel({ analysis, onUnlocked, onStartUnlock }) {
    const pricing = usePricing();
    const priceLabel = formatINR(pricing.current_paise);

    return (
        <div className="rounded-[28px] bg-white p-8 shadow-soft md:p-10" data-testid="unlock-panel">
            <div className="flex items-center justify-between gap-3">
                <div className="flex items-center gap-2 text-gold">
                    <Sparkles className="h-4 w-4" />
                    <span className="text-[11px] uppercase tracking-[0.25em]">Upgrade when you're ready</span>
                </div>
                <LaunchPill />
            </div>
            <h3 className="mt-4 font-serif text-3xl leading-tight text-charcoal md:text-4xl">
                Unlock the full strategy
            </h3>
            <p className="mt-4 text-[15px] text-ink">
                Complete outfit coordination, colour harmony, contrast balance, decor matching and a photo outcome prediction — for both of you. Plus a personalised Story Studio concept based on your relationship.
            </p>
            <div className="mt-6">
                <PriceLine tone="dark" size="lg" showBadge={false} testId="unlock-price" />
                <div className="mt-2 text-sm text-ink/70">one-time · instant access</div>
            </div>
            <button
                onClick={onStartUnlock}
                data-testid="unlock-button"
                className="btn-gold mt-8 w-full"
            >
                <Unlock className="mr-2 h-4 w-4" /> Tell your story · {priceLabel}
            </button>
            <p className="mt-3 text-center text-[11.5px] text-ink/70">
                3-minute Story Studio form → payment → full strategy unlocked
            </p>
            <p className="mt-3 flex items-center justify-center gap-1.5 text-xs text-ink/70">
                <Shield className="h-3.5 w-3.5" /> Secure payments via Razorpay
            </p>
        </div>
    );
}

export default function AnalysisResult() {
    const { id } = useParams();
    const [searchParams] = useSearchParams();
    const printMode = searchParams.get("pdf") === "1";
    const packParam = parseInt(searchParams.get("pack"), 10);
    const preselectedPack = [1, 2, 3, 4, 5].includes(packParam) ? packParam : null;
    const [analysis, setAnalysis] = useState(null);
    const [error, setError] = useState("");
    const [storyOpen, setStoryOpen] = useState(false);
    const [chosenPackSize, setChosenPackSize] = useState(null);
    const openStoryForPack = useCallback((size) => {
        setChosenPackSize(size);
        setStoryOpen(true);
    }, []);
    const unlockRef = useRef(null);
    const { current_paise } = usePricing();
    const priceLabel = formatINR(current_paise);

    const refresh = useCallback(async () => {
        try {
            const data = await getAnalysis(id);
            setAnalysis(data);
            setError("");
        } catch (err) {
            const status = err?.response?.status;
            // Only show "not found" on an actual 404. Transient network errors
            // (Cloudflare 502 during long-running polls) shouldn't blow away
            // the existing analysis state.
            if (status === 404) {
                setError(err?.response?.data?.detail || "Analysis not found.");
            }
        }
    }, [id]);

    useEffect(() => {
        refresh();
    }, [refresh]);

    // Poll while report is generating OR gallery is generating (after paid)
    // OR the paid pipeline hasn't cleared the final quality gate yet.
    useEffect(() => {
        if (!analysis) return;
        const pipelineFailed = FAILURE_STAGES.includes(analysis.pipeline_status);
        const needsPoll = analysis.report_status === "generating"
            || analysis.unlock_status === "unlocking"
            || analysis.unlock_status === "scheduled"
            || (analysis.paid && analysis.gallery_status === "generating")
            || (analysis.paid && analysis.storyboard_status === "generating")
            || (analysis.paid && analysis.video_status === "generating")
            || (analysis.paid && !analysis.report_ready && !pipelineFailed);
        if (!needsPoll) return;
        const t = setInterval(refresh, 3000);
        return () => clearInterval(t);
    }, [analysis, refresh]);

    if (error) {
        return (
            <div className="min-h-screen bg-ivory">
                <NavBar />
                <div className="mx-auto max-w-xl px-6 pt-40 text-center">
                    <h1 className="font-serif text-3xl text-charcoal">Analysis not found</h1>
                    <p className="mt-4 text-ink">{error}</p>
                    <Link to="/" className="btn-gold mt-8 inline-flex">Back to home</Link>
                </div>
            </div>
        );
    }

    if (!analysis) {
        return (
            <div className="min-h-screen bg-ivory">
                <NavBar />
                <div className="flex min-h-[60vh] items-center justify-center">
                    <Loader2 className="h-6 w-6 animate-spin text-charcoal/40" />
                </div>
            </div>
        );
    }

    const paid = Boolean(analysis.paid);
    const unlocking = analysis.unlock_status === "unlocking";
    const reportReady = Boolean(analysis.report_ready);
    const pipelineFailed = FAILURE_STAGES.includes(analysis.pipeline_status);
    const freeReport = analysis.free_report || null;
    const paidReport = analysis.paid_report || null;

    // We keep a ref to the unlock panel so the free-report "Unlock" button can scroll-focus it
    const scrollToUnlock = () => unlockRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });

    return (
        <div className="min-h-screen bg-ivory" data-testid="result-page">
            {!printMode && !paid && <NavBar />}
            <section className={printMode ? "pt-10 pb-10" : paid ? "pt-12 pb-24 md:pt-16" : "pt-32 pb-24 md:pt-40"}>
                <div className="mx-auto max-w-6xl px-6 md:px-12">
                    {!printMode && (
                        <Link to="/" className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.22em] text-ink/70 hover:text-charcoal" data-testid="back-home-from-result">
                            <ArrowLeft className="h-3.5 w-3.5" /> Back
                        </Link>
                    )}

                    {/* Sticky share / download (paid + not print mode) */}
                    {paid && !printMode && (
                        <div className="mt-2">
                            <ShareActionsSticky analysis={analysis} />
                        </div>
                    )}

                    {/* SCARCITY BANNER — top-of-page urgency (d) */}
                    {!paid && !printMode && <ScarcityBanner priceLabel={priceLabel} />}

                    <div className="mt-6 max-w-2xl">
                        <span className="eyebrow">{paid ? "Your full strategy" : "Build your wedding vision"}</span>
                        <h1 className="mt-4 font-serif text-4xl leading-tight text-charcoal md:text-5xl" data-testid="result-page-title">
                            {paid
                                ? "Here's your complete plan."
                                : buildPersonalisedHeadline(analysis)}
                        </h1>
                        <p className="mt-4 text-[16px] text-ink">
                            {paid
                                ? "Use this as the reference for what to wear, how to style, and how it will read on camera."
                                : "Confirm your event pack below to get your fully personalised styling strategy."}
                        </p>
                    </div>

                    {/* SOCIAL PROOF TICKER — c */}
                    {!paid && !printMode && <SocialProofTicker />}

                    {/* Legacy free_report rendering — preserved for records created
                       before the Emergent Update Patch (Oct 2026) removed free AI
                       generation. New submissions never populate report_status or
                       free_report, so these branches are dormant for new records. */}
                    {!paid && analysis.report_status === "generating" && (
                        <div className="mt-12 rounded-[28px] bg-white p-10 shadow-soft text-center" data-testid="generating-state">
                            <Loader2 className="mx-auto h-6 w-6 animate-spin text-gold" />
                            <h2 className="mt-6 font-serif text-2xl text-charcoal md:text-3xl">
                                Writing your strategy…
                            </h2>
                            <p className="mt-3 text-sm text-ink">
                                Your decision engine and skin-tone reading are ready. The styling report is being written now —
                                usually 45–90 seconds. This page will update automatically.
                            </p>
                        </div>
                    )}
                    {!paid && analysis.report_status === "failed" && (
                        <div className="mt-12 rounded-[28px] bg-rose-50 p-8 text-charcoal ring-1 ring-rose-100" data-testid="generating-failed">
                            <div className="text-[11px] uppercase tracking-[0.22em] text-rose-700">Report generation failed</div>
                            <h2 className="mt-3 font-serif text-2xl">We couldn't write your full report this time.</h2>
                            <p className="mt-3 text-sm text-ink">
                                Your skin tone and decision engine results are saved. Please reach out and we'll regenerate your strategy.
                            </p>
                        </div>
                    )}
                    {((!paid && analysis.report_status === "ready" && freeReport)
                        || (paid && !reportReady && freeReport)) && (
                        <div className="mt-12">
                            <FreeReport report={freeReport} analysis={analysis} onUnlock={scrollToUnlock} priceLabel={priceLabel} paid={paid} />
                        </div>
                    )}
                    {!paid && !analysis.report_status && freeReport && (
                        <div className="mt-12">
                            <FreeReport report={freeReport} analysis={analysis} onUnlock={scrollToUnlock} priceLabel={priceLabel} paid={paid} />
                        </div>
                    )}

                    {/* Emergent Update Patch (Oct 2026): no personalised free preview is
                       shown before payment — only the generic, zero-AI-cost sample
                       report link. */}
                    {!paid && <SampleReportTeaser />}

                    {paid && analysis.skin_tone && (
                        <div className="mt-8">
                            <SkinToneCard analysis={analysis} />
                        </div>
                    )}

                    {/* Unlock progress overlay (Step 1: Vowze, Step 2: gallery, Step 3: quality checks) */}
                    {(unlocking || paid) && !reportReady && !pipelineFailed && (
                        <div className="mt-8">
                            <UnlockProgress analysis={analysis} />
                        </div>
                    )}

                    {/* Quality-gate failure — friendly, non-technical, payment-safe messaging.
                       A paid report NEVER reaches the customer until report_ready === true.
                       Gated on `pipelineFailed` alone (not `unlocking || paid`) because once
                       generation fails, unlock_status flips to "failed" and paid stays false —
                       this state must still surface the failure, not silently disappear. */}
                    {pipelineFailed && (
                        <div className="mt-8">
                            <PipelineFailureState analysis={analysis} />
                        </div>
                    )}

                    {/* NEW conversion showcase: sneak-peek + content teasers + value stack + comparison + mega-CTA
                       Emergent Update Patch (Oct 2026): gated on `analysis.free` (the deterministic,
                       always-instant teaser) instead of the removed `freeReport` (Claude-written, no
                       longer generated) — checkout must never wait on free AI generation. */}
                    {!paid && analysis.free && analysis.unlock_status !== "unlocking" && analysis.unlock_status !== "scheduled" && (
                        <div className="mt-10 space-y-10">
                            <EventPacksPricing
                                analysis={analysis}
                                onPickPack={openStoryForPack}
                                unlockRef={unlockRef}
                                preselectedPack={preselectedPack}
                            />
                            <PaidUnlockShowcase
                                onStartUnlock={() => openStoryForPack(preselectedPack || analysis?.pack_size || 3)}
                            />
                        </div>
                    )}

                    {/* Wedding look gallery, paid report, and full report are ONLY
                       revealed once report_ready === true — the backend quality
                       gate has confirmed styling, images and text all pass. */}
                    {paid && reportReady && (
                        <div className="mt-10">
                            <WeddingLookGallery analysis={analysis} />
                        </div>
                    )}

                    {paid && reportReady && paidReport && (
                        <div className="mt-10 space-y-8">
                            <PhotoOutcomeHero analysis={analysis} paidReport={paidReport} />
                            <PaletteCardActions analysis={analysis} />
                            <ExecutiveSummary analysis={analysis} paidReport={paidReport} />
                            <PaidReport
                                report={paidReport}
                                gallery={analysis.gallery || {}}
                                storyboard={mergedStoryboard(analysis)}
                                storyboardStatus={analysis.storyboard_status}
                                heroVideoUrl={absGalleryUrl(analysis.hero_video_url)}
                                videoStatus={analysis.video_status}
                            />
                        </div>
                    )}

                    {paid && reportReady && !paidReport && (
                        <div className="mt-10">
                            <FullReport analysis={analysis} />
                        </div>
                    )}

                    {/* Share + Download (paid + not print mode) */}
                    {paid && !printMode && (
                        <div className="mt-10">
                            <ShareActionsInline analysis={analysis} />
                        </div>
                    )}
                </div>
            </section>

            {/* Story Studio modal — gates the payment flow */}
            {storyOpen && !paid && (
                <StoryStudioForm
                    analysis={analysis}
                    packSize={chosenPackSize}
                    onClose={() => setStoryOpen(false)}
                    onSubmitted={refresh}
                    onUnlockStart={refresh}
                />
            )}

            {/* Sticky mobile/desktop unlock bar (a) + exit-intent modal (e) */}
            {!paid && !printMode && analysis.free && (
                <>
                    <StickyUnlockBar
                        priceLabel={priceLabel}
                        onUnlock={() => openStoryForPack(preselectedPack || analysis?.pack_size || 3)}
                    />
                    <ExitIntentModal
                        priceLabel={priceLabel}
                        onUnlock={() => openStoryForPack(preselectedPack || analysis?.pack_size || 3)}
                        analysis={analysis}
                    />
                </>
            )}
        </div>
    );
}
